import styles from "./header.module.css";

import { inject, observer } from "mobx-react";
import { SensorDataStore } from "../../store/store";
import { useTranslation } from "react-i18next";
import { TabItem, TabPanel } from "../../../../components/tab-panel";
import { AuthStore, FeatureStore, TransitionStore } from "../../../../store";
import AddButton from "../../../../components/buttons/AddButton";
import { runInAction } from "mobx";
import { ExpandedState } from "../../../../components/table/table";
import LocationSelectorStore from "../../../../store/location-selector-store";
import UserSettingsStore from "../../../../store/user-settings";
import SearchBar from "../../../../components/text-input-debounced/DebouncedSearchBar";
import LocationSelector, {
    LocationItem,
} from "../../../../components/location-selector/LocationSelector";
import { TreeNodeItem } from "../../../../components/tree-select/TreeSelect";
import { Search } from "../../../../components/svg";

export interface HeaderProps {
    sensorDataStore?: SensorDataStore;
    authStore?: AuthStore;
    transitionStore?: TransitionStore;
    featureStore?: FeatureStore;
    locationSelectorStore?: LocationSelectorStore;
    userSettingsStore?: UserSettingsStore;
}

function Header(props: HeaderProps) {
    const { t } = useTranslation("sensor-grid");
    const { t: searchSensorTranslation } = useTranslation("search-sensor");
    const sensorDataStore = props.sensorDataStore!;
    const transitionStore = props.transitionStore!;
    const authStore = props.authStore!;
    const featureStore = props.featureStore!;
    const locationSelectorStore = props.locationSelectorStore!;

    const render = () => {
        const locationDropdownData = buildLocationDropdownData();

        return (
            <div className={styles["manage-sensors-page-header"]}>
                <div className={styles["manage-sensors-tabs"]}>
                    <TabPanel activeKey={"manage-sensors"}>
                        <TabItem
                            itemKey={"manage-sensors"}
                            label={t("Manage Sensors")}
                        />
                    </TabPanel>
                </div>
                <div className={styles["manage-sensors-filter-inputs"]}>
                    <div
                        className={
                            styles["manage-sensors-input-container-filter"]
                        }
                    >
                        <LocationSelector
                            mandatory
                            selectedId={sensorDataStore.filteringLocationId}
                            locationList={locationDropdownData}
                            onValueChange={(value: LocationItem) => {
                                sensorDataStore.setFilteringLocationId(
                                    value.locationId!
                                );
                            }}
                            emptyValueLabel={searchSensorTranslation(
                                "AllRooms"
                            )}
                        />
                    </div>
                    <div
                        className={
                            styles["manage-sensors-input-container-filter"]
                        }
                    >
                        <div className={styles["search-bar-container"]}>
                            <SearchBar
                                onDebounceChange={(text) => {
                                    sensorDataStore.setSearchValue(text);
                                }}
                                onValueChange={(text) => {
                                    sensorDataStore.setSearchDisplay(text);
                                }}
                                value={sensorDataStore.searchDisplay}
                                placeHolder={searchSensorTranslation(
                                    "Search sensor"
                                )}
                                icon={Search}
                            />
                        </div>
                    </div>
                    {addButton()}
                </div>
            </div>
        );
    };

    const buildLocationDropdownData = () => {
        const dropdownData = locationSelectorStore.selectedRoomList.map(
            (room: TreeNodeItem) => {
                return {
                    locationId: room.id,
                    locationName: room.name,
                    areaName: room.path || "",
                };
            }
        );

        return dropdownData;
    };

    const addButton = () => {
        return (
            authStore.isAdmin() && (
                <div className={styles["add-button-container"]}>
                    <AddButton
                        disabled={
                            transitionStore.isLoading ||
                            sensorDataStore.expandedSensorIndex !==
                                ExpandedState.COLLAPSED
                        }
                        onClick={() => {
                            sensorDataStore.setNewSensor();
                            setLocationForNewSensor();
                            ofeliaOverrideSensorsType();
                        }}
                    >
                        {t("Add Sensor")}
                    </AddButton>
                </div>
            )
        );
    };

    const ofeliaOverrideSensorsType = () => {
        if (featureStore.featureList.onlyVirtualSensorAvailable) {
            const { selectedSensor } = sensorDataStore;
            runInAction(() => {
                selectedSensor.sensorType = "VirtualSensor";
            });
        }
    };

    const setLocationForNewSensor = () => {
        if (sensorDataStore.filteringLocationId) {
            const data = locationSelectorStore.selectedRoomList.find(
                (r) => r.id === sensorDataStore.filteringLocationId
            );

            if (data) {
                const { selectedSensor } = sensorDataStore;
                runInAction(() => {
                    selectedSensor.locationId = data.id;
                    selectedSensor.locationName = data.name;
                    selectedSensor.areaName = data.path;
                });
            }
        }
    };

    return render();
}

export default inject(
    "sensorDataStore",
    "authStore",
    "transitionStore",
    "featureStore",
    "locationSelectorStore",
    "userSettingsStore"
)(observer(Header));
